import GMPAPI from "../../services/gmpapi";
import { addSeconds } from "date-fns";

export default {
  namespaced: false,
  state: {
    apitoken: undefined,
    apitokenExpires: undefined,
    refreshtoken: undefined,
    userinfo: undefined,
    accounts: undefined,
    currentAccount: undefined,
    rememberme: false,
    ghostAccounts: undefined,
    lastUserRefresh: undefined,
  },
  // Actions are for asynchronous tasks. Do something, then commit a mutation
  actions: {
    /** Use the GMP api to login a username, and retrieve / save the user data and access token */
    async LoginUser({ commit, dispatch }, { username, password, stayloggedin }) {
      // Wipe any OTP login first
      commit("clearPayment");
      // Choose to save in local vs session
      commit("setRememberme", stayloggedin);

      const { access_token, refresh_token, expires_in } = await GMPAPI.GetTokenUsername(username, password, stayloggedin);
      commit("setRefresh", refresh_token);
      commit("setToken", access_token);
      commit("setApiTokenExpires", addSeconds(new Date(), expires_in));
      await dispatch("FetchUserInfo");
    },

    /** Attempt to get a new auth token using the refresh token */
    async LoginWithRefresh({ commit, state }) {
      const { access_token, expires_in } = await GMPAPI.GetRefreshedToken(state.refreshtoken, state.rememberme);
      console.log("User auth refreshed");
      commit("setToken", access_token);
      commit("setApiTokenExpires", addSeconds(new Date(), expires_in));
    },

    /** Use the GMP api to login a username, and retrieve / save the user data and access token */
    async LoginOTP({ commit, dispatch }, { accountNumber, phoneLast }) {
      // Wipe any OTP login first
      commit("clearPayment");
      // Choose to save in local vs session
      commit("setRememberme", false);
      const { access_token, expires_in } = await GMPAPI.GetTokenOTP(accountNumber, phoneLast);
      commit("setRefresh", undefined);
      commit("setToken", access_token);
      commit("setApiTokenExpires", addSeconds(new Date(), expires_in));
      await dispatch("FetchUserInfo");
    },

    /** Attempt to login with the cookie (we can't see it because it is httponly) */
    async AttemptCookieLogin({ commit, dispatch }) {
      // Wipe any OTP login first
      commit("clearPayment");
      // Choose to save in local vs session
      commit("setRememberme", false);
      try {
        await dispatch("FetchUserInfo");
      } catch (err) {
        // Cookie login not working
        await dispatch("LogOut");
        throw err;
      }
    },

    async FetchGhostingAccount({ commit }, accountNumber) {
      const accounts = await GMPAPI.GetAccounts([accountNumber]);
      if (!accounts || accounts.length === 0) {
        throw new Error("No matching accounts found");
      }
      accounts[0].photoUrl = "/wp-content/themes/gmptwentynineteen/assets/images/ghosting.png";
      commit("pushGhostAccount", accounts[0]);
      commit("setCurrentAccount", accounts[0]);
    },

    /** Update the current user's data, accounts, and account info */
    async FetchUserInfo({ commit, state }) {
      const userinfo = await GMPAPI.GetCurrentUser();
      let accounts = [];
      // Only pull info if the user has any accounts (ghosting users may have none)
      // Note that energyAccounts can be either undefined or empty array
      if (userinfo.customData.energyAccounts && userinfo.customData.energyAccounts.length) {
        const accountNumbers = userinfo.customData.energyAccounts.map(item => item.accountNumber);
        accounts = await GMPAPI.GetAccounts(accountNumbers);
        // Attach user data to user accounts
        for (const energyAccount of userinfo.customData.energyAccounts) {
          const account = accounts.find(item => item.accountNumber === energyAccount.accountNumber);
          if (account) {
            account.isPrimary = energyAccount.isPrimary;
            account.nickname = energyAccount.nickname;
            account.photoUrl = energyAccount.photoUrl;
            if (!account.photoUrl) account.photoUrl = "/wp-content/themes/gmptwentynineteen/assets/images/default-account.png";
          } else {
            console.error("Unknown user account number: " + energyAccount.accountNumber);
          }
        }
      }

      let selectedAccount;
      if (accounts.length) {
        // If nothing else, select the first account
        selectedAccount = accounts[0];
        // Then try to select the "primary" account
        for (const account of accounts) {
          if (account.isPrimary) {
            selectedAccount = account;
          }
        }
        // If there was a previously selected account, select that one
        if (state.currentAccount) {
          const currentNum = state.currentAccount.accountNumber;
          for (const account of accounts) {
            if (account.accountNumber === currentNum) {
              selectedAccount = account;
            }
          }
        }
      }
      // Refresh all ghosted accounts
      if (state.ghostAccounts && state.ghostAccounts.length && state.currentAccount) {
        try {
          const ghosts = await GMPAPI.GetAccounts(state.ghostAccounts.map(item => item.accountNumber));
          for (const ghosted of ghosts) {
            ghosted.photoUrl = "/wp-content/themes/gmptwentynineteen/assets/images/ghosting.png";
          }
          commit("setGhostAccounts", ghosts);
        } catch (err) {
          console.error("Unable to refresh ghosted accts");
        }

        // Finally, if a ghosted account was selected, pick that one
        if (state.currentAccount) {
          const currentNum = state.currentAccount.accountNumber;
          for (const account of state.ghostAccounts) {
            if (account.accountNumber === currentNum) {
              selectedAccount = account;
            }
          }
        }
      }
      commit("setUser", userinfo);
      commit("setAccounts", accounts);
      commit("setCurrentAccount", selectedAccount);
      commit("setLastRefresh");
    },

    async LogOut({ commit, state }) {
      commit("setUser", undefined);
      commit("setAccounts", undefined);
      commit("clearGhostAccounts");
      commit("setCurrentAccount", undefined);
      commit("setRememberme", false);
      commit("clearPayment");
      commit("setLoginDrawerVisible", false);
      commit("setAccountSwitcherVisible", false);
      const waiters = [GMPAPI.LogoutCookie()];
      if (state.refreshtoken) {
        waiters.push(GMPAPI.RevokeRefreshToken(state.refreshtoken));
      }
      await Promise.all(waiters);
      commit("setToken", undefined);
      commit("setRefresh", undefined);
      commit("setApiTokenExpires", undefined);
      // This is a bit nuclear but good while we're changing what is saved
      window.localStorage.clear();
      window.sessionStorage.clear();
    },
  },
  // Changes the state
  mutations: {
    setToken(state, token) {
      state.apitoken = token;
    },
    setRefresh(state, token) {
      state.refreshtoken = token;
    },
    setUser(state, userinfo) {
      state.userinfo = userinfo;
    },
    setAccounts(state, accounts) {
      state.accounts = accounts;
    },
    setCurrentAccount(state, account) {
      state.currentAccount = account;
    },
    setRememberme(state, remember) {
      state.rememberme = remember;
    },
    clearGhostAccounts(state) {
      state.ghostAccounts = undefined;
    },
    pushGhostAccount(state, account) {
      if (!state.ghostAccounts) {
        state.ghostAccounts = [account];
      } else {
        state.ghostAccounts.unshift(account);
        if (state.ghostAccounts.length > 5) {
          state.ghostAccounts.pop();
        }
      }
    },
    setGhostAccounts(state, accounts) {
      state.ghostAccounts = accounts;
    },
    setLastRefresh(state) {
      state.lastUserRefresh = new Date().toISOString();
    },
    clearLastRefresh(state) {
      state.lastUserRefresh = undefined;
    },
    setApiTokenExpires(state, date) {
      if (date) {
        state.apitokenExpires = date.toISOString();
      } else {
        state.apitokenExpires = undefined;
      }
    },
  },
}